import React, { useContext, useEffect, useState } from "react";
import { User } from "../models/User";
import { addRole, getUsers } from "../service/AuthService";
import {
    Alert,
    Box,
    Button,
    Container,
    Paper,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import { useAuth, UserContextType } from "../auth";

export const ManageUsers = () => {
    const { IsAdmin, cookies } = useContext<UserContextType>(useAuth);
    const [users, setUsers] = useState<User[]>([]);
    const [open, setOpen] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (IsAdmin && cookies?.UserAuth?.token) {
            fetchUsers();
        }
    }, [IsAdmin, cookies]);

    const fetchUsers = () => {
        setLoading(true);
        if (cookies?.UserAuth?.token) {
            getUsers(
                cookies.UserAuth.token,
                (response: any) => {
                    setUsers(response.data);
                    setLoading(false);
                },
                () => {
                    setErrorMessage("Failed to load users.");
                    setOpen(true);
                    setLoading(false);
                }
            );
        }
    };

    const handleAddRole = (email: string, roleName: string) => {
        if (cookies?.UserAuth?.token) {
            addRole(
                cookies.UserAuth.token,
                email,
                roleName,
                (response: any) => {
                    setSuccessMessage(`Successfully added ${roleName} role to ${email}`);
                    setOpen(true);
                    fetchUsers();
                },
                () => {
                    setErrorMessage(`Failed to add ${roleName} role to ${email}`);
                    setOpen(true);
                }
            );
        }
    };

    const handleClose = (
        event?: React.SyntheticEvent | Event,
        reason?: string
    ) => {
        if (reason === "clickaway") {
            return;
        }
        setOpen(false);
        setSuccessMessage("");
        setErrorMessage("");
    };

    if (!IsAdmin) {
        return (
            <Typography variant="h5" color="error" align="center" sx={{ mt: 4 }}>
                Access Denied
            </Typography>
        );
    }

    return (
        <Box sx={{ p: 3, pt: 10 }}>
            <Typography variant="h4" gutterBottom>
                Manage Users
            </Typography>

            {errorMessage && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert>}
            {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}

            <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }} aria-label="user table">
                    <TableHead>
                        <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>Roles</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {users.map((user) => (
                            <TableRow
                                key={user.id}
                                sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                            >
                                <TableCell component="th" scope="row">
                                    {user.firstName} {user.lastName}
                                </TableCell>
                                <TableCell>{user.email}</TableCell>
                                <TableCell>{user.roles.join(", ")}</TableCell>
                                <TableCell align="right">
                                    {!user.roles.includes("Admin") && (
                                        <Button
                                            variant="contained"
                                            size="small"
                                            sx={{ mr: 1 }}
                                            onClick={() => handleAddRole(user.email, "Admin")}
                                        >
                                            Make Admin
                                        </Button>
                                    )}
                                    {!user.roles.includes("Admin") && !user.roles.includes("PageEditor") && (
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={() => handleAddRole(user.email, "PageEditor")}
                                        >
                                            Make Page Editor
                                        </Button>
                                    )}
                                    {!user.roles.includes("Admin") && !user.roles.includes("GroupAdmin") && (
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            sx={{ ml: 1 }}
                                            onClick={() => handleAddRole(user.email, "GroupAdmin")}
                                        >
                                            Make Group Admin
                                        </Button>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Snackbar open={open} autoHideDuration={6000} onClose={handleClose}>
                <Alert
                    onClose={handleClose}
                    severity={errorMessage ? "error" : "success"}
                    sx={{ width: "100%" }}
                >
                    {errorMessage || successMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
};
